/**
 * EventsCalendar — public /events page.  Chronological list of every
 * active event, grouped by month.  Deliberately list-based (not a
 * grid) so it stays readable on mobile.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, ArrowRight, Sparkles, ArrowLeft, LayoutGrid, LogIn, UserPlus, Ticket, ShoppingCart,
} from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import BrandMark from '../components/BrandMark';
import ThemeToggle from '../components/ThemeToggle';
import PageHero from '../components/PageHero';
import CountryConfirmDialog, { getSavedCountry } from '../components/CountryConfirmDialog';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { useCart } from '../context/CartContext';
import { CartButton } from '../components/CartDrawer';

// Parse "Feb 22" or "Mar 04" into a Date this year (approximate — good
// enough for grouping / sorting).  If parsing fails, put it at the end.
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const parseDateLabel = (label) => {
  if (!label) return null;
  const [m, d] = label.trim().split(/\s+/);
  const mi = MONTHS.indexOf(m);
  const day = parseInt(d, 10);
  if (mi < 0 || Number.isNaN(day)) return null;
  const now = new Date();
  let year = now.getFullYear();
  // Month already past this year? assume next year.
  if (mi < now.getMonth() || (mi === now.getMonth() && day < now.getDate())) year += 1;
  return new Date(year, mi, day);
};

const groupByMonth = (events) => {
  const groups = new Map();
  events.forEach((ev) => {
    const d = parseDateLabel(ev.date_label);
    const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'later';
    const label = d ? `${MONTHS[d.getMonth()].toUpperCase()} · ${d.getFullYear()}` : 'LATER';
    if (!groups.has(key)) groups.set(key, { label, items: [] });
    groups.get(key).items.push({ ...ev, _date: d });
  });
  // Sort groups chronologically, then each group by date asc.
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([_, v]) => ({ ...v, items: v.items.sort((a, b) => (a._date?.getTime() || 0) - (b._date?.getTime() || 0)) }));
};

const EventsCalendar = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countryOpen, setCountryOpen] = useState(false);
  const [pending, setPending] = useState(null);

  useDocumentMeta({
    title: 'Events Calendar — Freeze Dry Academy',
    description: 'Upcoming freeze-dry webinars, course drops and community meet-ups. Reserve your seat.',
    keywords: 'freeze dry events, freeze drying webinars, freeze dryer meetup, freeze dry community calendar',
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/events`, { params: { limit: 100 } });
        if (alive) setEvents(data?.items || []);
      } catch (_) { /* ignore */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [API]);

  const grouped = useMemo(() => groupByMonth(events), [events]);

  const handleRsvp = (ev) => {
    setPending(ev);
    if (getSavedCountry()) recordRsvp(ev, getSavedCountry());
    else setCountryOpen(true);
  };

  const recordRsvp = async (ev, country) => {
    try {
      await axios.post(`${API}/public/events/${ev.id}/rsvp`, {
        event_id: ev.id, country,
        referer: typeof window !== 'undefined' ? window.location.pathname : null,
      });
    } catch (_) { /* ignore */ }
    navigate(ev.cta_link || '/login?mode=signup');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="events-calendar-page">
      <header className="border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="md" subtitle="Events" onClick={() => navigate('/')} />
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-foreground/80">
            <button onClick={() => navigate('/platform')} className="hover:text-foreground transition">Platform</button>
            <button onClick={() => navigate('/events')} className="text-foreground">Events</button>
            <button onClick={() => navigate('/shop')} className="hover:text-foreground transition" data-testid="events-nav-shop">Shop</button>
            <button onClick={() => navigate('/directory')} className="hover:text-foreground transition">Directory</button>
            <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition">Pricing</button>
          </nav>
          <div className="flex items-center gap-2">
            <CartButton />
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="rounded-full font-semibold hidden sm:inline-flex">
              <LogIn className="w-3.5 h-3.5 mr-1.5" /> Sign in
            </Button>
            <Button size="sm" onClick={() => navigate('/login?mode=signup')} className="rounded-full font-semibold">
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Sign up
            </Button>
          </div>
        </div>
      </header>

      <PageHero
        eyebrow="Events"
        eyebrowIcon={Calendar}
        title={<>Upcoming <span className="text-amber-brand">events</span></>}
        description="Live webinars, course drops and in-person meet-ups — everything happening at the academy in one calendar."
        backTo="/" backLabel="Home" testId="events-calendar-hero"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button size="lg" onClick={() => navigate('/login?mode=signup')} className="rounded-full font-extrabold px-6">
              Sign up free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/platform')}
              className="rounded-full font-extrabold px-6 bg-background/10 border-background/30 text-background hover:bg-background/20 hover:text-background"
            >
              Explore platform
            </Button>
          </div>
        }
      />

      <main className="container mx-auto px-6 py-16 max-w-4xl">
        {loading && <div className="text-center text-sm text-muted-foreground py-12">Loading events…</div>}

        {!loading && grouped.length === 0 && (
          <div className="pro-card p-14 text-center" data-testid="events-calendar-empty">
            <div className="h-12 w-12 rounded-full bg-muted mx-auto flex items-center justify-center">
              <Calendar className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="mt-4 font-heading text-xl font-extrabold tracking-tight">No events on the calendar right now</div>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
              We publish new webinars and community meet-ups every couple of weeks. Sign up free and we&apos;ll ping you the moment the next one drops.
            </p>
            <Button className="mt-5 rounded-full font-extrabold" onClick={() => navigate('/login?mode=signup')}>
              Sign up free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {!loading && grouped.map((group) => (
          <section key={group.label} className="mb-12" data-testid={`events-month-${group.label.replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{group.label}</h2>
              <div className="ml-auto text-xs uppercase tracking-widest text-muted-foreground">
                {group.items.length} event{group.items.length === 1 ? '' : 's'}
              </div>
            </div>
            <ul className="space-y-3">
              {group.items.map((ev, i) => (
                <li
                  key={ev.id}
                  className="pro-card p-5 flex items-start gap-4 hover:border-primary/60 transition-colors group animate-fade-up"
                  style={{ '--i': i }}
                  data-testid={`calendar-event-${ev.id}`}
                >
                  <div className="w-16 flex-shrink-0 text-center rounded-lg bg-primary/10 text-primary py-2">
                    <div className="text-[10px] uppercase tracking-widest font-extrabold">{(ev.date_label || '').split(' ')[0]}</div>
                    <div className="font-heading text-2xl font-extrabold leading-none">{(ev.date_label || '').split(' ')[1]}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-primary font-extrabold">
                      <Sparkles className="w-3 h-3" /> {ev.kind}
                    </div>
                    <h3 className="mt-1 font-heading text-lg font-bold tracking-tight">{ev.title}</h3>
                    {ev.body && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{ev.body}</p>}
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      {ev.is_ticketed && ev.price_cents > 0 ? (
                        <button
                          type="button"
                          onClick={() => { navigate('/shop'); }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3.5 py-1.5 text-xs font-extrabold hover:bg-primary/90 transition-colors"
                          data-testid={`calendar-event-buy-${ev.id}`}
                        >
                          <Ticket className="w-3 h-3" /> Buy ticket · ${(ev.price_cents / 100).toFixed(2)}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRsvp(ev)}
                          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors"
                          data-testid={`calendar-event-cta-${ev.id}`}
                        >
                          {ev.cta_label || 'Reserve seat'} <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      {ev.rsvp_count > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                          {ev.rsvp_count} operator{ev.rsvp_count === 1 ? '' : 's'} attending
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </main>

      <section className="bg-navy-spotlight text-background">
        <div className="container mx-auto px-6 py-16 text-center max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-background/20 bg-background/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-background/70">
            <LayoutGrid className="w-3 h-3" /> Never miss a drop
          </div>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
            Get every event <span className="text-amber-brand">in your inbox.</span>
          </h2>
          <p className="mt-3 text-background/70 leading-relaxed">
            Sign up free and we&apos;ll ping you the moment a new webinar or meet-up is announced.
          </p>
          <Button size="lg" onClick={() => navigate('/login?mode=signup')} className="mt-6 rounded-full font-extrabold px-6 bg-primary text-primary-foreground hover:bg-primary/90">
            Sign up free <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border bg-background">
        <div className="container mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <BrandMark size="sm" onClick={() => navigate('/')} />
            <span>© {new Date().getFullYear()} Freeze Dry Academy · Peak and Prairie Industries Corp.</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={() => navigate('/platform')} className="hover:text-foreground transition">Platform</button>
            <button onClick={() => navigate('/events')} className="hover:text-foreground transition">Events</button>
            <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition">Pricing</button>
            <button onClick={() => navigate('/directory')} className="hover:text-foreground transition">Directory</button>
          </div>
        </div>
      </footer>

      <CountryConfirmDialog
        open={countryOpen}
        onOpenChange={setCountryOpen}
        eventTitle={pending?.title}
        onConfirm={(country) => { if (pending) recordRsvp(pending, country); }}
      />
    </div>
  );
};

export default EventsCalendar;
